<script lang="ts">
  import { onDestroy, untrack } from "svelte"
  import { mdiEmailArrowRightOutline } from "@mdi/js"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import { type DailyBriefingPreferences } from "../../../../../../../../api/kehrblatt/membership-settings"
  import SendBriefingDialog from "./_modules/send-briefing-dialog"
  import { sendDailyBriefing } from "../../../../../../../../api/kehrblatt/daily-briefing"

  const {
    organizationId,
    timezone,
    recipient,
    canSend,
    isSelf,
    organizationUnsettled,
    pageSaving = false,
    onStateChange,
    savePreferences,
    contextSignal,
    isCurrent,
  } = $props<{
    organizationId: string
    timezone: string
    recipient: {
      membershipId: string
      label: string
      email: string
      preferences: DailyBriefingPreferences
    }
    canSend: boolean
    isSelf: boolean
    organizationUnsettled: boolean
    pageSaving?: boolean
    onStateChange: (state: { dirty: boolean; busy: boolean }) => void
    savePreferences:
      | ((
          value: DailyBriefingPreferences,
          signal: AbortSignal,
        ) => Promise<DailyBriefingPreferences>)
      | null
    contextSignal: AbortSignal
    isCurrent: () => boolean
  }>()
  const bound = untrack(() => ({ contextSignal, isCurrent, savePreferences }))
  const readDraft = (value: DailyBriefingPreferences) => ({
    enabled: String(value.enabled),
    includePdf: String(value.include_pdf),
    includeOperations:
      value.include_operations === null ? "default" : String(value.include_operations),
  })
  let lastSaved = $state(untrack(() => readDraft(recipient.preferences)))
  let draft = $state(untrack(() => ({ ...lastSaved })))
  let saving = $state(false)
  let sending = $state(false)
  let error = $state("")
  let notice = $state("")
  let showSendDialog = $state(false)
  let sendError = $state("")
  let attempt: { date: string; id: string } | null = null
  let active = true
  const controller = new AbortController()
  const dirty = $derived(
    draft.enabled !== lastSaved.enabled ||
      draft.includePdf !== lastSaved.includePdf ||
      draft.includeOperations !== lastSaved.includeOperations,
  )
  const busy = $derived(saving || sending)
  const disabled = $derived(busy || pageSaving)
  const editDisabled = $derived(disabled || !bound.savePreferences)
  const foreignOptout = $derived(!isSelf && lastSaved.enabled === "false")
  const sendBlockedReason = $derived(
    dirty || organizationUnsettled
      ? "Bitte zuerst speichern"
      : foreignOptout
        ? "Dieser Empfänger hat die Morgenübersicht abbestellt."
        : "",
  )
  const activationOptions = [
    { value: "true", label: "Aktiviert" },
    { value: "false", label: "Deaktiviert" },
  ]
  $effect(() => onStateChange({ dirty, busy }))
  const invalidate = () => {
    active = false
    controller.abort()
    draft = { ...lastSaved }
    showSendDialog = false
    attempt = null
    error = notice = sendError = ""
  }
  bound.contextSignal.addEventListener("abort", invalidate, { once: true })
  if (bound.contextSignal.aborted) invalidate()
  // effect-ok: invalidation - a replacement binding cannot inherit an existing draft or send attempt.
  $effect(() => {
    if (
      contextSignal !== bound.contextSignal ||
      isCurrent !== bound.isCurrent ||
      savePreferences !== bound.savePreferences
    )
      invalidate()
  })
  onDestroy(() => {
    active = false
    controller.abort()
    bound.contextSignal.removeEventListener("abort", invalidate)
  })

  /** Save this recipient's draft for the page action, using the normalized server result. */
  export async function saveChanges(): Promise<boolean> {
    if (!dirty) return true

    if (!active || !bound.isCurrent() || busy || !bound.savePreferences) return false

    saving = true
    error = notice = ""
    try {
      const result = await bound.savePreferences(
        {
          enabled: draft.enabled === "true",
          include_pdf: draft.includePdf === "true",
          include_operations:
            draft.includeOperations === "default" ? null : draft.includeOperations === "true",
        },
        controller.signal,
      )

      if (!active || !bound.isCurrent()) return false

      lastSaved = readDraft(result)
      draft = { ...lastSaved }
      notice = "Persönliche Einstellungen gespeichert."

      return true
    } catch {
      if (active) error = "Speichern fehlgeschlagen. Ihre Änderungen bleiben erhalten."

      return false
    } finally {
      if (active) saving = false
    }
  }
  async function send(referenceDate: string) {
    if (!active || !bound.isCurrent() || !canSend || disabled || sendBlockedReason) return

    sending = true
    sendError = notice = ""

    if (!attempt || attempt.date !== referenceDate) {
      attempt = { date: referenceDate, id: crypto.randomUUID() }
    }

    try {
      await sendDailyBriefing(
        organizationId,
        recipient.membershipId,
        attempt.id,
        controller.signal,
        referenceDate,
      )

      if (!active || !bound.isCurrent()) return

      attempt = null
      showSendDialog = false
      notice = recipient.email
        ? `Versand an ${recipient.email} wurde angestoßen.`
        : "Versand an Ihre Konto-E-Mail-Adresse wurde angestoßen."
    } catch {
      if (active) sendError = "Der Versand konnte nicht bestätigt werden. Bitte erneut versuchen."
    } finally {
      if (active) sending = false
    }
  }
</script>

<section class="settings-stack" aria-label={recipient.label}>
  <SectionTitle
    title={recipient.label}
    description={recipient.email}
    brightBackground={true}
    small={true}
  >
    {#snippet buttonSnippet()}
      {#if canSend}
        <CTA
          type="secondaryButton"
          content={sending ? "Wird angefordert …" : "Jetzt senden"}
          icon={mdiEmailArrowRightOutline}
          darkMode={false}
          onClick={() => {
            sendError = ""
            showSendDialog = true
          }}
          disabled={disabled || Boolean(sendBlockedReason)}
          tooltipContent="Datum wählen und die Übersicht einmalig an die hinterlegte E-Mail-Adresse senden. Der automatische Versand bleibt unverändert."
        />
      {/if}
    {/snippet}
  </SectionTitle>
  <div class="box-content form">
    <div class="settings-row">
      <div class="field-block">
        <Input
          type="select"
          placeholder="Morgenübersicht erhalten"
          bind:value={draft.enabled}
          options={activationOptions}
          clearable={false}
          brightBackground={true}
          disabled={editDisabled}
          tooltip="Automatische Morgenübersicht an die hinterlegte Konto-E-Mail-Adresse senden."
        />
      </div>
      <div class="field-block">
        <Input
          type="select"
          placeholder="Tagesplan als PDF"
          bind:value={draft.includePdf}
          options={activationOptions}
          clearable={false}
          brightBackground={true}
          disabled={editDisabled}
          tooltip="Jeder versendeten Übersicht den Tagesplan anhängen, auch ohne eigene Termine. Bei Teamzugriff enthält das PDF zusätzlich die freigegebenen Mitarbeiterpläne; der persönliche Plan steht zuerst."
        />
      </div>
      <div class="field-block">
        <Input
          type="select"
          placeholder="Betriebsübersicht"
          bind:value={draft.includeOperations}
          options={[{ value: "default", label: "Standard" }, ...activationOptions]}
          clearable={false}
          brightBackground={true}
          disabled={editDisabled}
          tooltip="Inhalte bleiben auf die vorhandenen Datenberechtigungen begrenzt."
        />
      </div>
    </div>
  </div>
  {#if error}<p role="alert">{error}</p>{/if}
  {#if notice}<p role="status">{notice}</p>{/if}
  {#if canSend && sendBlockedReason}<p class="helper-text">{sendBlockedReason}</p>{/if}
</section>
{#if showSendDialog}
  <SendBriefingDialog
    {timezone}
    recipientLabel={recipient.label}
    recipientEmail={recipient.email}
    {sending}
    disabled={!canSend || disabled || Boolean(sendBlockedReason)}
    error={sendError}
    onSend={send}
    onClose={() => (showSendDialog = false)}
  />
{/if}

<style>
  .settings-stack,
  .box-content {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
    min-width: 0;
  }
  .settings-stack {
    padding-top: 1rem;
    overflow-wrap: anywhere;
  }
  .settings-row {
    display: grid;
    width: 100%;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 13rem), 1fr));
    gap: 0.9rem;
  }
  .field-block {
    min-width: 0;
  }
  p {
    margin: 0;
    color: var(--text-invers-100);
  }
  .helper-text {
    font-size: 0.85rem;
  }
  [role="alert"] {
    border-left: 3px solid var(--red-300);
    padding: 0.6rem 0.8rem;
  }
</style>
